import { useCallback, useEffect, useMemo, useState } from 'react'
import uiStates, {
  UiStateKey,
  UiStateScope,
} from '../../../../../lib/commons/uiStates'
import _ from 'lodash'
import { ColumnState } from 'ag-grid-community'
import useWindowUnloadEffect from '../../../../hooks/useWindowUnloadEffect'
import { getUrlQueryObject } from '../../../../../utils/urls'
import { useColumnAndFilterStateStorageService } from '../../../../../services/storage-services/ui-states/columnAndFilterStateStorageService'
import { BulkSheetViewColumnAndFilterState } from '../../../../model/bulkSheetColumnAndFilterState'

export type BulkSheetState = {
  expandedRowIds?: string[]
  columnState?: ColumnState[]
  filterState?: { [key: string]: any }
}

export type RestoreType = 'EXPAND_ROW_IDS' | 'COLUMN_STATE' | 'FILTER_STATE'
export const URL_PARAMS_SHARE_SAVED_UI_STATE: string = 'arg'

/**
 * Control
 *
 * @param functionUuid
 * @param key
 */
export const useBulkSheetState = (
  functionUuid: string,
  key: string,
  uiStateKey?: UiStateKey,
  restoreTypes?: RestoreType[],
  projectUuid?: string
): {
  initialized: boolean
  restored: boolean
  restoredAt: number | undefined
  finishRestoring: () => void
  expandedRowIds: string[]
  expandRows: (_: string[]) => void
  collapseRows: (_: string[]) => void
  columnState: ColumnState[]
  saveColumnState: (s: ColumnState[]) => void
  filterState?: { [key: string]: any }
  saveFilterState: (s: { [key: string]: any }) => void
  saveImmediately: () => void
} => {
  const [expandedRowIds, setExpandedRowIds] = useState<string[]>([])
  const [columnState, setColumnState] = useState<ColumnState[]>([])
  const [filterState, setFilterState] = useState<{ [key: string]: any }>({})
  const [initialized, setInitialized] = useState(false)
  const [restored, setRestored] = useState(false)
  const [restoredAt, setRestoredAt] = useState<number | undefined>(undefined)

  const columnAndFilterStateStorageService =
    useColumnAndFilterStateStorageService(functionUuid, projectUuid || '')

  const stateKey = useMemo(
    () => `${uiStateKey ?? UiStateKey.BulkSheetState}-${key}`,
    [uiStateKey, key]
  )

  // If default argument, it will become a new variable every time, so memoize it.
  const resotreParams = useMemo(
    () => restoreTypes ?? ['EXPAND_ROW_IDS', 'COLUMN_STATE', 'FILTER_STATE'],
    [restoreTypes]
  )

  const restoreSavedUiState = useCallback(async (): Promise<
    BulkSheetViewColumnAndFilterState | undefined
  > => {
    if (!projectUuid) return
    const urlObject = getUrlQueryObject()
    if (!urlObject) return undefined
    const code =
      URL_PARAMS_SHARE_SAVED_UI_STATE in urlObject
        ? urlObject[URL_PARAMS_SHARE_SAVED_UI_STATE]
        : undefined
    if (!code) return undefined
    const permanentUiState =
      await columnAndFilterStateStorageService.getByCode(code)
    if (!permanentUiState) return undefined
    return permanentUiState.value
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectUuid, columnAndFilterStateStorageService.getByCode])

  // Function definitions.
  const restoreState = useCallback(async () => {
    try {
      const restoredSavedState =
        resotreParams.includes('COLUMN_STATE') ||
        resotreParams.includes('FILTER')
          ? await restoreSavedUiState()
          : undefined

      const response = await uiStates.get({
        applicationFunctionUuid: functionUuid,
        key: stateKey,
        scope: UiStateScope.User,
      })
      const fetchedUiState = response.json
      if (!fetchedUiState.value) {
        return
      }
      const fetchedState: BulkSheetState = JSON.parse(fetchedUiState.value)
      resotreParams.includes('EXPAND_ROW_IDS') &&
        setExpandedRowIds(fetchedState.expandedRowIds ?? [])
      resotreParams.includes('COLUMN_STATE') &&
        setColumnState(
          restoredSavedState?.columnState
            ? restoredSavedState.columnState
            : (fetchedState.columnState ?? [])
        )
      resotreParams.includes('FILTER_STATE') &&
        setFilterState(
          restoredSavedState?.filterState
            ? restoredSavedState.filterState
            : (fetchedState.filterState ?? {})
        )
      setRestoredAt(new Date().getTime())
    } catch (e) {
      // ignore error not to stop service while presentational api is not working correctly.
    } finally {
      setInitialized(true)
    }
  }, [functionUuid, stateKey, resotreParams])

  // Fetch initial row state.
  useEffect(() => {
    restoreState()
  }, [restoreState])

  const save = useCallback(
    _.debounce(state => {
      uiStates.update(
        {
          key: stateKey,
          scope: UiStateScope.User,
          value: JSON.stringify(state),
        },
        functionUuid
      )
    }, 3000),
    [functionUuid, stateKey]
  )

  // Save on row state changed, with debounce.
  useEffect(() => {
    if (!initialized) return
    save({ expandedRowIds, columnState, filterState })
  }, [save, initialized, expandedRowIds, columnState, filterState])

  const saveImmediately = useCallback(() => {
    uiStates.update(
      {
        key: stateKey,
        scope: UiStateScope.User,
        value: JSON.stringify({ expandedRowIds, columnState, filterState }),
      },
      functionUuid
    )
  }, [stateKey, expandedRowIds, columnState, filterState, functionUuid])

  // Save before window unload.
  useWindowUnloadEffect(saveImmediately, false)

  // Exposed functions.
  const expandRows = useCallback(
    (rowIds: string[]) => {
      if (!restored) return
      setExpandedRowIds(current => {
        return _.uniq([...current, ...rowIds])
      })
    },
    [restored]
  )

  const collapseRows = useCallback(
    (rowIds: string[]) => {
      if (!restored) return
      setExpandedRowIds(current => current.filter(id => !rowIds.includes(id)))
    },
    [restored]
  )

  const saveColumnState = useCallback(
    (s: ColumnState[]) => {
      if (!restored) return
      setColumnState(s)
    },
    [restored]
  )

  const saveFilterState = useCallback(
    s => {
      if (!restored) return
      setFilterState(s)
    },
    [restored]
  )

  const finishRestoring = useCallback(() => setRestored(true), [])

  const result = useMemo(
    () => ({
      initialized,
      restored,
      restoredAt,
      finishRestoring,
      expandedRowIds,
      expandRows,
      collapseRows,
      columnState,
      saveColumnState,
      filterState,
      saveFilterState,
      saveImmediately,
    }),
    [
      initialized,
      restored,
      restoredAt,
      finishRestoring,
      expandedRowIds,
      expandRows,
      collapseRows,
      columnState,
      saveColumnState,
      filterState,
      saveFilterState,
      saveImmediately,
    ]
  )

  return result
}
